/* eslint-disable @typescript-eslint/no-unused-vars */

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { SelectionAlwaysOnDisplay } from '@lexical/react/LexicalSelectionAlwaysOnDisplay';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import clsx from 'clsx';
import { $getRoot } from 'lexical';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';

import { POST_SET_VALUE_OPTIONS } from '@cms/utils/constants';
import { EditorKey } from '@cms/utils/contexts/editorSettings';
import { JobFormData } from '@cms/utils/forms/job';
import { TemplateFormData } from '@cms/utils/forms/template';
import { generateSearchParam } from '@cms/utils/helpers';
import { StoriesPath } from '@cms/utils/routes';
import { setEditorState } from '@cms/utils/store/slices/editor.slice';
import { useEditorSettings } from '../../utils/contexts/EditorSettingsContext';
import { useSharedHistoryContext } from '../../utils/contexts/SharedHistoryContext';
import { StoryFormData } from '../../utils/forms/story';
import { useAppDispatch, useAppSelector } from '../../utils/store/hooks';
import { FormDescription } from '../data/shared';
import { LexicalAutoLinkPlugin as AutoLinkPlugin } from './plugins/AutoLinkPlugin';
import { ClipboardPastePlugin } from './plugins/ClipboardPastePlugin';
import { CodeActionMenuPlugin } from './plugins/CodeActionMenuPlugin';
import { ComponentPickerMenuPlugin } from './plugins/ComponentPickerPlugin';
import { FloatingLinkEditorPlugin } from './plugins/FloatingLinkEditorPlugin';
import { FloatingTextFormatToolbarPlugin } from './plugins/FloatingTextFormatToolbarPlugin';
import { ShortcutsPlugin } from './plugins/ShortcutsPlugin';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import { EditorSpeedDial } from './shared/EditorSpeedDial';
import { GeneralSettingsComponent } from './shared/GeneralSettings';
import { LexicalContentEditable as ContentEditable } from './ui/ContentEditable';
import { contentElementsToLexical, lexicalToContentElements } from './utils/helpers';

const HeadlinesComponent = () => {
    const { watch, register } = useFormContext<StoryFormData>()

    const { mode } = useAppSelector((state) => state.editor);
    const [
        headline,
        // subheadline
    ] = watch([
        'headline',
        // 'subheadline'
    ])

    return (
        <div className='mx-auto mt-8 flex flex-col justify-center items-center placeholder:text-cs-900 text-cs-900 my-2'>
            <TextareaAutosize
                {...register('headline')}
                value={headline || ''}
                className={clsx(
                    "w-full text-center outline-none text-[28px] px-8",
                    'resize-none'
                )}
                placeholder='Add your headline here...'
                disabled={mode === 'readonly'}
                minRows={1}
            />
            {/* <TextareaAutosize
                {...register('subheadline')}
                value={subheadline || ''}
                className={clsx(
                    "w-full text-center outline-none text-[24px] px-8",
                    'resize-none'
                )}
                placeholder='Add your subheadline here...'
                disabled={mode === 'readonly'}
                minRows={1}
            /> */}
        </div>
    )
}

const FloatingGeneralSettings = () => {
    return (
        <div className='rounded-lg overflow-hidden shadow-lg'>
            <div className='bg-cs-900 text-white p-3'>
                General Settings
            </div>
            <div className='w-full'>
                <GeneralSettingsComponent
                    presentationVersion='compact'
                />
            </div>
        </div>
    )
}

export interface EditorProps {
    isReadonly?: boolean;
    editorKey: EditorKey;
}

export const EditorContent: React.FC<EditorProps> = (props) => {
    const { editorKey, isReadonly = false } = props;

    const { historyState } = useSharedHistoryContext();
    const {
        settings: {
            [editorKey]: {
                hasFloatingGeneralSettings,
                selectionAlwaysOnDisplay,
                showSpeedDial,
                hasHeadlines,
                styles
            }
        },
    } = useEditorSettings();

    const [editor] = useLexicalComposerContext();

    const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
    const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

    const isSmallWidthViewport = useMediaQuery('(max-width: 1024px)')

    const onRef = (_floatingAnchorElem: HTMLDivElement) => {
        if (_floatingAnchorElem !== null) {
            setFloatingAnchorElem(_floatingAnchorElem);
        }
    };

    return (
        <div
            className={clsx(
                "editor-shell",
                isReadonly && 'opacity-50 pointer-events-none',
                styles.shell
            )}
        >
            <ToolbarPlugin
                editor={editor}
                setIsLinkEditMode={setIsLinkEditMode}
                editorKey={editorKey}
            />

            <div
                className={clsx(
                    'editor-container',
                    'grid grid-cols-3 gap-10',
                    styles.container
                )}
            >
                {hasFloatingGeneralSettings && (
                    <div
                        className={clsx(
                            'hidden xl:block',
                            'relative my-2',
                            'col-span-1',
                        )}
                    >
                        <div
                            className={clsx(
                                'sticky top-32',
                                'w-full'
                            )}
                        >
                            <FloatingGeneralSettings />
                        </div>
                    </div>
                )}

                <div
                    className={clsx(
                        'relative',
                        hasFloatingGeneralSettings ? 'xl:col-span-2 col-span-3' : 'col-span-full',
                    )}
                >
                    {hasHeadlines && <HeadlinesComponent />}

                    <ShortcutsPlugin
                        editor={editor}
                        setIsLinkEditMode={setIsLinkEditMode}
                    />
                    <ClipboardPastePlugin editor={editor} />
                    <HistoryPlugin externalHistoryState={historyState} />
                    <ComponentPickerMenuPlugin />
                    <MarkdownShortcutPlugin />
                    <ClearEditorPlugin />
                    <AutoFocusPlugin />
                    <AutoLinkPlugin />
                    <LinkPlugin />
                    <ListPlugin />
                    <TablePlugin
                        hasCellMerge
                        hasTabHandler
                        hasHorizontalScroll
                        hasCellBackgroundColor
                    />

                    <TabIndentationPlugin maxIndent={7} />

                    {selectionAlwaysOnDisplay && <SelectionAlwaysOnDisplay />}

                    {floatingAnchorElem && (
                        <>
                            <FloatingLinkEditorPlugin
                                anchorElem={floatingAnchorElem}
                                isLinkEditMode={isLinkEditMode}
                                setIsLinkEditMode={setIsLinkEditMode}
                            />
                            {!isSmallWidthViewport && (
                                <>
                                    <CodeActionMenuPlugin anchorElem={floatingAnchorElem} />
                                    <FloatingTextFormatToolbarPlugin
                                        editorKey={editorKey}
                                        anchorElem={floatingAnchorElem}
                                        setIsLinkEditMode={setIsLinkEditMode}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <RichTextPlugin
                        contentEditable={
                            <div className="editor-scroller">
                                <div className="editor" ref={onRef}>
                                    <ContentEditable
                                        placeholder="Enter some content..."
                                        className={`ContentEditable__root type-${editorKey}`}
                                    />
                                </div>

                            </div>
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    {showSpeedDial && <EditorSpeedDial />}
                </div>
            </div>
        </div>
    )
}

export const StoryEditor: React.FC<Omit<EditorProps, "editorKey">> = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const {
        story,
        mode,
        hasContentElementsLoaded,
        hasContentElementsChanged
    } = useAppSelector((state) => state.editor);

    const story_id = searchParams.get(generateSearchParam(StoriesPath, "id"));

    const [editor] = useLexicalComposerContext();

    const [shouldStartListeningToChanges, setShouldStartListeningToChanges] = useState<boolean>(false);

    /** Skip marking content as changed when we sync from story (e.g. after save) */
    const isSyncingContentFromStoryRef = useRef<boolean>(false);

    /** 
     * Since tracking lexical state in the form is complex
     * we use those flags to consider if the story is loaded 
     * to start tracking changes in the editor state
    */
    useEffect(() => {
        if (story_id) {
            if (story) {
                isSyncingContentFromStoryRef.current = true;
                contentElementsToLexical(editor, story.content_elements || []);
                setTimeout(() => {
                    isSyncingContentFromStoryRef.current = false;
                }, 100);
                dispatch(setEditorState({
                    hasContentElementsLoaded: true
                }));
            }
        }
        else {
            dispatch(setEditorState({
                hasContentElementsLoaded: true
            }));
        }

    }, [story_id, story])

    useEffect(() => {
        if (!story) {
            editor.update(() => {
                const root = $getRoot();
                root.clear();
            });
        }
    }, [story, editor])

    useEffect(() => {
        if (hasContentElementsLoaded) {
            setTimeout(() => {
                setShouldStartListeningToChanges(true)
            }, 500);
        }
        else {
            setShouldStartListeningToChanges(false)
        }
    }, [hasContentElementsLoaded])

    useEffect(() => {
        if (mode === 'editing') {
            editor.setEditable(true);
        }
        else {
            editor.setEditable(false);
        }
    }, [mode, editor])

    useEffect(() => {
        const unregister = editor.registerUpdateListener((payload) => {
            if (isSyncingContentFromStoryRef.current) return;
            if (
                shouldStartListeningToChanges
                &&
                !hasContentElementsChanged
                &&
                payload.dirtyElements.size > 0
            ) {
                dispatch(setEditorState({
                    hasContentElementsChanged: true
                }))
            }
        })

        return () => {
            unregister();
        }
    }, [
        editor,
        hasContentElementsChanged,
        shouldStartListeningToChanges,
    ]);

    return (
        <EditorContent editorKey="story_editor" isReadonly={mode === 'readonly'} />
    )
}

export const TemplateEditor: React.FC<Omit<EditorProps, "editorKey">> = () => {
    const { setValue } = useFormContext<TemplateFormData>()

    return (
        <>
            <EditorContent editorKey="template_editor" />
            <OnChangePlugin
                onChange={(editorState, editor) => {
                    editorState.read(() => {
                        setValue('content_elements', lexicalToContentElements(editor), POST_SET_VALUE_OPTIONS)
                    });
                }}
            />
        </>
    )
}

export const JobEditor: React.FC<Omit<EditorProps, "editorKey">> = () => {
    const { setValue } = useFormContext<JobFormData>()

    return (
        <>
            <div className='self-start'>
                <FormDescription description="Role description" required />
            </div>
            <EditorContent editorKey="job_editor" />
            <OnChangePlugin
                onChange={(editorState, editor) => {
                    editorState.read(() => {
                        setValue('content_elements', lexicalToContentElements(editor), {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                        })
                    });
                }}
            />
        </>
    )
}
